// Bolsas de horas de soporte (D82; antes en Notion "Soporte - Bolsas de Horas"). Objetos bolsaHoras y movimientoBolsa
// de la app. El saldo sale de los movimientos (recargas menos consumos). Al registrar horas facturables en un caso de
// una empresa con bolsa vigente se descuentan solas (movimiento CONSUMO con el registroHorasId).
// D94: solo existen los 3 planes de QuickBooks (SERVICE FEE TABLE): Emergency, Support 6 y Support 12. Las bolsas que
// vinieron de Notion quedan como ANTERIOR hasta que venzan. Cada servicio cobra minimo 20 min; en horario extendido
// (7-9 am, 5-7 pm) +50 % y fuera de horario +100 %. Support 12 da 2 h por mes del plan y no se acumulan.
import { create, f, get, listAll, remove } from './rest';

export type Plan = 'EMERGENCY' | 'SUPPORT_6' | 'SUPPORT_12' | 'ANTERIOR';
export type Horario = 'NORMAL' | 'EXTENDIDO' | 'FUERA';
export type Bolsa = {
  id: string;
  cliente: string;
  empresaId: string | null;
  empresa: string | null;
  inicio: string | null;
  validez: string | null;
  notas: string;
  plan: Plan;
  factura: string;
  documento: string;
};
export type Movimiento = {
  id: string;
  bolsaId: string | null;
  tipo: 'RECARGA' | 'CONSUMO';
  minutos: number;
  fecha: string;
  concepto: string;
  atendidoPor: string;
  referencia: string;
  casoId: string | null;
  registroHorasId: string | null;
  horario?: Horario | null;
  minutosReales?: number | null;
};
export type Semaforo = 'verde' | 'amarillo' | 'rojo';
/** Un mes de Support 12: lo que tenia, lo que se uso y lo que se perdio sin usar (si ya paso). */
export type MesPlan = { desde: number; hasta: number; disponibles: number; usados: number; vencidos: number };
export type Saldo = {
  recargados: number;
  consumidos: number;
  restantes: number;
  vencida: boolean;
  semaforo: Semaforo;
  /** Dias que faltan para que venza la bolsa (null si no tiene fecha). */
  venceEn?: number | null;
  /** Support 12: el mes del plan en curso y todos los meses hasta hoy. */
  mes?: MesPlan | null;
  meses?: MesPlan[];
};

/** Los 3 productos de QuickBooks. minutos = lo que trae la bolsa (en Support 12, cada mes). */
export const PLANES: Record<Exclude<Plan, 'ANTERIOR'>, { nombre: string; minutos: number; dias: number | null; meses: number | null; precio: number; hora: number; mensual: boolean; detalle: string }> = {
  EMERGENCY: { nombre: 'Emergency', minutos: 120, dias: 90, meses: null, precio: 240, hora: 120, mensual: false, detalle: '2 h · vigencia 90 días' },
  SUPPORT_6: { nombre: 'Support 6', minutos: 600, dias: 180, meses: null, precio: 850, hora: 85, mensual: false, detalle: '10 h · vigencia 180 días' },
  SUPPORT_12: { nombre: 'Support 12', minutos: 120, dias: null, meses: 12, precio: 1440, hora: 60, mensual: true, detalle: '2 h cada mes · 12 meses, no se acumulan' },
};
export const nombrePlan = (p: Plan) => (p === 'ANTERIOR' ? 'Anterior' : PLANES[p].nombre);
/** Hora adicional de Support 12 (US$70, producto de QuickBooks; Juan confirmo que existe): suma horas al mes del plan. */
export const ADICIONAL_S12 = true;
export const HORA_ADICIONAL = 70;
/** En Support 12 toda recarga es una hora adicional (el plan no lleva recarga inicial: son 2 h por mes). */
export const esAdicional = (m: Pick<Movimiento, 'tipo'>, plan: Plan) => plan === 'SUPPORT_12' && m.tipo === 'RECARGA';
export const MINIMO_MIN = 20;
export const RECARGO: Record<Horario, number> = { NORMAL: 1, EXTENDIDO: 1.5, FUERA: 2 };
export const HORARIOS: [Horario, string, string][] = [
  ['NORMAL', 'Normal', 'L–V 9 am a 5 pm'],
  ['EXTENDIDO', 'Extendido +50 %', '7–9 am · 5–7 pm'],
  ['FUERA', 'Fuera de horario +100 %', 'noches y fines de semana'],
];

type RawBolsa = {
  id: string;
  name: string | null;
  empresaId: string | null;
  empresa?: { name?: string } | null;
  inicio: string | null;
  validez: string | null;
  notas: string | null;
  tipoPlan?: Plan | null;
  factura?: string | null;
  documento?: string | null;
};
type RawMov = {
  id: string;
  name: string | null;
  bolsaId: string | null;
  tipo: 'RECARGA' | 'CONSUMO' | null;
  minutos: number | null;
  fecha: string | null;
  atendidoPor: string | null;
  referencia: string | null;
  casoId: string | null;
  registroHorasId: string | null;
  horario?: Horario | null;
  minutosReales?: number | null;
  createdAt: string;
};
const aBolsa = (r: RawBolsa): Bolsa => ({
  id: r.id,
  cliente: r.name ?? '',
  empresaId: r.empresaId,
  empresa: r.empresa?.name ?? null,
  inicio: r.inicio,
  validez: r.validez,
  notas: r.notas ?? '',
  plan: r.tipoPlan ?? 'ANTERIOR',
  factura: r.factura ?? '',
  documento: r.documento ?? '',
});
const aMov = (r: RawMov): Movimiento => ({
  id: r.id,
  bolsaId: r.bolsaId,
  tipo: r.tipo ?? 'CONSUMO',
  minutos: r.minutos ?? 0,
  fecha: r.fecha ?? r.createdAt,
  concepto: r.name ?? '',
  atendidoPor: r.atendidoPor ?? '',
  referencia: r.referencia ?? '',
  casoId: r.casoId,
  registroHorasId: r.registroHorasId,
  horario: r.horario ?? null,
  minutosReales: r.minutosReales ?? null,
});

export const bolsas = async () => (await listAll<RawBolsa>('bolsasHoras', { orderBy: 'name[AscNullsLast]', depth: 1 }, 1000)).map(aBolsa);
export const movimientos = async (bolsaId?: string) =>
  (await listAll<RawMov>('movimientosBolsa', { filter: bolsaId ? f.eq('bolsaId', bolsaId) : undefined, orderBy: 'fecha[DescNullsLast]' }, 20000)).map(aMov);

/* ---------------- reglas (puras) ---------------- */

const DIA = 86_400_000;
const BOGOTA = -5 * 3_600_000; // Colombia no cambia de hora
/** Dia (año, mes, dia) de una fecha en hora de Bogota. */
const diaBogota = (t: number) => {
  const d = new Date(t + BOGOTA);
  return [d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()] as const;
};
/** Mediodia de Bogota de ese dia (asi se guardan las fechas de las bolsas). */
const mediodia = (y: number, m: number, d: number) => Date.UTC(y, m, d, 12) - BOGOTA;
const medianoche = (y: number, m: number, d: number) => Date.UTC(y, m, d) - BOGOTA;

/** Ultimo dia de vigencia (ISO, mediodia de Bogota): Emergency 90 dias, Support 6 180 dias, Support 12 12 meses. */
export function vigenciaDe(plan: Exclude<Plan, 'ANTERIOR'>, inicio: string) {
  const [y, m, d] = diaBogota(Date.parse(inicio));
  const p = PLANES[plan];
  return new Date(p.meses ? mediodia(y, m + p.meses, d - 1) : mediodia(y, m, d + (p.dias ?? 0) - 1)).toISOString();
}

/** Minutos que se descuentan: minimo 20 por servicio y luego el recargo del horario. */
export const minutosACobrar = (min: number, horario: Horario = 'NORMAL') => (min > 0 ? Math.round(Math.max(MINIMO_MIN, min) * RECARGO[horario]) : 0);

/** Horario segun la hora de Bogota: L-V 9-17 normal; 7-9 y 17-19 extendido; el resto y fines de semana, fuera. */
export function horarioSugerido(fecha: Date | number = Date.now()): Horario {
  const d = new Date((typeof fecha === 'number' ? fecha : fecha.getTime()) + BOGOTA);
  const dia = d.getUTCDay();
  const h = d.getUTCHours() + d.getUTCMinutes() / 60;
  if (dia === 0 || dia === 6) return 'FUERA';
  if (h >= 9 && h < 17) return 'NORMAL';
  if ((h >= 7 && h < 9) || (h >= 17 && h < 19)) return 'EXTENDIDO';
  return 'FUERA';
}

/** Los 12 meses de un Support 12, desde el dia de inicio (15 sep – 14 oct, 15 oct – 14 nov…). */
export function mesesDelPlan(inicio: string, meses = 12) {
  const [y, m, d] = diaBogota(Date.parse(inicio));
  return Array.from({ length: meses }, (_, k) => [medianoche(y, m + k, d), medianoche(y, m + k + 1, d)] as const);
}

type DatosBolsa = Pick<Bolsa, 'plan' | 'inicio' | 'validez'>;
/**
 * Saldo y semaforo. Anterior (Notion): rojo sin saldo o vencida; amarillo con menos del 20 % o menos de 2 h.
 * Planes: amarillo con menos del 25 % o si vence en 30 dias. Support 12: el saldo es el del mes del plan en curso
 * (2 h mas las adicionales de ese mes, menos lo usado ese mes); lo que sobra de un mes se pierde.
 */
export function saldoDe(movs: Movimiento[], b: DatosBolsa | string | null, ahora = Date.now()): Saldo {
  const bolsa: DatosBolsa = typeof b === 'object' && b ? b : { plan: 'ANTERIOR', inicio: null, validez: b };
  const vencida = !!bolsa.validez && Date.parse(bolsa.validez) + DIA <= ahora;
  const venceEn = bolsa.validez ? Math.ceil((Date.parse(bolsa.validez) + DIA - ahora) / DIA) : null;
  const suma = (tipo: Movimiento['tipo'], desde = -Infinity, hasta = Infinity) =>
    movs.filter((m) => m.tipo === tipo && (desde === -Infinity || (Date.parse(m.fecha) >= desde && Date.parse(m.fecha) < hasta))).reduce((s, m) => s + m.minutos, 0);

  if (bolsa.plan === 'SUPPORT_12' && bolsa.inicio) {
    const todos = mesesDelPlan(bolsa.inicio).map(([desde, hasta]): MesPlan => {
      const disponibles = PLANES.SUPPORT_12.minutos + suma('RECARGA', desde, hasta);
      const usados = suma('CONSUMO', desde, hasta);
      return { desde, hasta, disponibles, usados, vencidos: hasta <= ahora ? Math.max(0, disponibles - usados) : 0 };
    });
    const meses = todos.filter((x) => x.desde <= ahora);
    const mes = todos.find((x) => x.desde <= ahora && ahora < x.hasta) ?? null;
    const recargados = mes?.disponibles ?? 0;
    const consumidos = mes?.usados ?? 0;
    const restantes = recargados - consumidos;
    const semaforo: Semaforo = vencida || !mes || restantes <= 0 ? 'rojo' : restantes < recargados * 0.25 || (venceEn != null && venceEn <= 30) ? 'amarillo' : 'verde';
    return { recargados, consumidos, restantes, vencida, semaforo, venceEn, mes, meses };
  }

  const recargados = suma('RECARGA');
  const consumidos = suma('CONSUMO');
  const restantes = recargados - consumidos;
  const poco = bolsa.plan === 'ANTERIOR' ? restantes < Math.max(120, recargados * 0.2) : restantes < recargados * 0.25 || (venceEn != null && venceEn <= 30);
  const semaforo: Semaforo = vencida || restantes <= 0 ? 'rojo' : poco ? 'amarillo' : 'verde';
  return { recargados, consumidos, restantes, vencida, semaforo, venceEn };
}

/** La bolsa de la empresa vigente en esa fecha (la que vence primero si hay varias). */
export function bolsaVigente(lista: Bolsa[], empresaId: string | null, fecha: string): Bolsa | null {
  if (!empresaId) return null;
  const t = Date.parse(fecha);
  const vigentes = lista.filter((b) => b.empresaId === empresaId && (!b.inicio || Date.parse(b.inicio) <= t + DIA) && (!b.validez || t < Date.parse(b.validez) + DIA));
  return vigentes.sort((a, b) => (a.validez ? Date.parse(a.validez) : Infinity) - (b.validez ? Date.parse(b.validez) : Infinity))[0] ?? null;
}

/* ---------------- escritura ---------------- */

export const crearBolsa = (b: { cliente: string; empresaId: string | null; inicio: string | null; validez: string | null; notas?: string; plan?: Plan; factura?: string; documento?: string }) =>
  create<{ id: string }>('bolsasHoras', {
    name: b.cliente.trim(),
    empresaId: b.empresaId,
    inicio: b.inicio,
    validez: b.validez,
    notas: b.notas ?? '',
    ...(b.plan ? { tipoPlan: b.plan } : {}),
    factura: b.factura ?? '',
    documento: b.documento ?? '',
  });

/** Bolsa nueva de uno de los 3 planes: la vigencia y las horas salen del plan (Support 12 no lleva recarga: son 2 h por mes). */
export async function crearBolsaPlan(b: { cliente: string; empresaId: string | null; plan: Exclude<Plan, 'ANTERIOR'>; inicio: string; factura?: string; documento?: string; notas?: string; quien: string }) {
  const p = PLANES[b.plan];
  const creada = await crearBolsa({ ...b, validez: vigenciaDe(b.plan, b.inicio) });
  if (!p.mensual)
    await crearMovimiento({ bolsaId: creada.id, tipo: 'RECARGA', minutos: p.minutos, fecha: b.inicio, concepto: `${p.nombre} contratado`, atendidoPor: b.quien, referencia: b.factura ?? '', casoId: null, registroHorasId: null });
  return creada;
}

export const crearMovimiento = (m: Omit<Movimiento, 'id'>) =>
  create<{ id: string }>('movimientosBolsa', {
    name: m.concepto.trim() || (m.tipo === 'RECARGA' ? 'Recarga' : 'Soporte'),
    bolsaId: m.bolsaId,
    tipo: m.tipo,
    minutos: Math.round(m.minutos),
    fecha: m.fecha,
    atendidoPor: m.atendidoPor,
    referencia: m.referencia,
    casoId: m.casoId,
    registroHorasId: m.registroHorasId,
    ...(m.horario ? { horario: m.horario } : {}),
    ...(m.minutosReales != null ? { minutosReales: Math.round(m.minutosReales) } : {}),
  });

/** Horas adicionales de Support 12: se suman al mes del plan de esa fecha (no pasan al mes siguiente). */
export const comprarAdicionales = (a: { bolsaId: string; horas: number; fecha: string; factura: string; quien: string }) =>
  crearMovimiento({
    bolsaId: a.bolsaId,
    tipo: 'RECARGA',
    minutos: a.horas * 60,
    fecha: a.fecha,
    concepto: `Hora adicional Support 12 × ${a.horas}`,
    atendidoPor: a.quien,
    referencia: a.factura,
    casoId: null,
    registroHorasId: null,
  });

/** Empresa del caso y su bolsa vigente (para el dialogo de horas, antes de guardar). */
export async function bolsaDelCaso(casoId: string, fecha: string) {
  const caso = await get<{ empresaId: string | null; numero: number | null }>('casos', casoId, 0);
  return { caso, bolsa: bolsaVigente(await bolsas(), caso.empresaId, fecha) };
}

/**
 * Despues de registrar horas en un caso: si son facturables y la empresa del caso tiene bolsa vigente, se descuentan
 * con el minimo de 20 min y el recargo del horario. Devuelve la bolsa y su saldo nuevo (para avisar), o null.
 */
export async function descontarDeBolsa(r: { registroId: string; casoId: string | null; facturable: boolean; minutos: number; fecha: string; descripcion: string; quien: string; horario?: Horario }) {
  if (!r.casoId || !r.facturable || r.minutos <= 0) return null;
  const { caso, bolsa } = await bolsaDelCaso(r.casoId, r.fecha);
  if (!bolsa) return null;
  const horario = r.horario ?? 'NORMAL';
  await crearMovimiento({
    bolsaId: bolsa.id,
    tipo: 'CONSUMO',
    minutos: minutosACobrar(r.minutos, horario),
    minutosReales: r.minutos,
    horario,
    fecha: r.fecha,
    concepto: r.descripcion,
    atendidoPor: r.quien,
    referencia: caso.numero ? `Caso #${caso.numero}` : '',
    casoId: r.casoId,
    registroHorasId: r.registroId,
  });
  return { bolsa, saldo: saldoDe(await movimientos(bolsa.id), bolsa) };
}

/** Al borrar un registro de horas se borra su consumo de la bolsa. */
export async function quitarConsumoDe(registroId: string) {
  const ms = await listAll<RawMov>('movimientosBolsa', { filter: f.eq('registroHorasId', registroId) }, 10);
  for (const m of ms) await remove('movimientosBolsa', m.id);
}

export const borrarMovimiento = (id: string) => remove('movimientosBolsa', id);
